import React from 'react';
import { IProps, IFormValue, IState } from './types';
import { Formik, Form, Field, ErrorMessage, FormikProps, FieldArray } from 'formik';
import { RouterSettings } from 'constants/models/routerSettings';
import { Code, Inscat, MortgageBankCategoryContent, MortgageBankContent } from 'bff/models/info';
import service from 'bff/services/memberCorrectionService';
import _ from 'lodash';
import { InsType } from 'constants/enums/insType.enum';
import { ClaimPolicyNoContent, EndorseOptContent, EndorseOptContentData, EndorseOptContentTarget } from 'bff/models/member';
import { EndorseType } from 'constants/enums/endorseType';
import commonService from 'utils/commonService';
import { EndorseTarget } from 'constants/enums/endorseTarget.enum';
import CorrectionEndorseItem from '../CorrectionEndorseItem';
import { EndorseOption } from 'constants/models/endorseOption';
import { EndorseColType } from 'constants/enums/endorseColType';
import * as Yup from 'yup';
import { mobileRegex } from 'utils/commonValidations';
import { EndorseAttachmentType } from 'constants/enums/endorseAttachmentType';
import FormikFocusError from 'components/FormikFocusError';
import storageService from 'utils/storageService';
import { storageKeys } from 'constants/enums/storageKeys.enum';
import InsTypeDefines from 'constants/models/insTypeDefines';
import { YesNo, YesNoType } from 'constants/enums/yesNo.enum';

let formInitialValues: IFormValue = {
  INS_TYPE: InsType.Fire,
  POLICY_NUMBER: '',
  APPLY_NO: '',
  FULL_ADDRESS: '',
  ENDORSE_OPTS: new Array(0),
  ORIGINAL_ENDORSE: {
    OPTION: new Array(0),
    FILES: new Array(0)
  }
};

const formValidationSchema = Yup.object().shape({
  INS_TYPE: Yup.string().required('此為必填欄位'),
  POLICY_NUMBER: Yup.string().required('此為必填欄位'),
  ENDORSE_OPTS: Yup.array().of(
    Yup.object().shape({
      FILE_FIELD_NAME: Yup.string().test('required', '此為必填欄位', function (val) {
        const { CHECKED, ATTACHMENT } = this.parent;
        if (CHECKED && ATTACHMENT === EndorseAttachmentType.Required) {
          return val;
        }

        return true;
      }),
      POST_CODE: Yup.string().test('required', '此為必填欄位', function (val) {
        const { path, parent } = this;
        const { CHECKED, DATA_TYPE } = parent;
        let errMsg = '';

        if (CHECKED && DATA_TYPE === EndorseColType.ADDRESS && !val) {
          errMsg = '此為必填欄位';
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.ADDRESS && val && val.length > 6) {
          errMsg = '郵遞區號長度不可超過 6 字元';
        }

        return errMsg ? this.createError({ path, message: errMsg }) : true;
      }),
      CITY_ID: Yup.string().test('required', '此為必填欄位', function (val) {
        const { CHECKED, DATA_TYPE } = this.parent;
        if (CHECKED && DATA_TYPE === EndorseColType.ADDRESS) {
          return val;
        }

        return true;
      }),
      AREA_ID: Yup.string().test('required', '此為必填欄位', function (val) {
        const { CHECKED, DATA_TYPE } = this.parent;
        if (CHECKED && DATA_TYPE === EndorseColType.ADDRESS) {
          return val;
        }

        return true;
      }),
      ADDRESS: Yup.string().test('has error', '檢核錯誤', function (val) {
        const { path, parent } = this;
        const { CHECKED, DATA_TYPE } = parent;
        let errMsg = '';

        if (CHECKED && DATA_TYPE === EndorseColType.ADDRESS && !val) {
          errMsg = '此為必填欄位';
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.ADDRESS && val && val.length > 200) {
          errMsg = '詳細地址長度不可超過 200 字元';
        }

        return errMsg ? this.createError({ path, message: errMsg }) : true;
      }),
      MOBILE: Yup.string().test('has error', '檢核錯誤', function (val) {
        const { path, parent } = this;
        const { CHECKED, DATA_TYPE } = parent;
        let errMsg = '';

        if (CHECKED && DATA_TYPE === EndorseColType.MOBILE && !val) {
          errMsg = '此為必填欄位';
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.MOBILE && val && val.length !== 10) {
          errMsg = '手機號碼長度為 10 字元';
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.MOBILE && val && !mobileRegex.test(val)) {
          errMsg = '手機格式不符';
        }

        return errMsg ? this.createError({ path, message: errMsg }) : true;
      }),
      MORGAGE_INFOS: Yup.array().when('CHECKED', (checked: any, schema: any) => {
        return !checked ? schema : schema.of(Yup.object().shape({
          HDR_ID_CODE: Yup.string().required('此為必填欄位'),
          HDR_ID: Yup.string().required('此為必填欄位'),
          // HBRCH_ID: Yup.string().required('此為必填欄位'),
          DELIVER_BANK: Yup.string().required('此為必填欄位'),
          OPERATOR: Yup.string().when("DELIVER_BANK", (deliverType: any, schema: any) => {
            return deliverType && deliverType === YesNo.Yes ? schema.required('此為必填欄位') : schema
          }),
          POSTCODE: Yup.string().when("DELIVER_BANK", (deliverType: any, schema: any) => {
            return deliverType && deliverType === YesNo.Yes ? schema.required('此為必填欄位') : schema
          }),
          CITY_ID: Yup.string().when("DELIVER_BANK", (deliverType: any, schema: any) => {
            return deliverType && deliverType === YesNo.Yes ? schema.required('此為必填欄位') : schema
          }),
          AREA_ID: Yup.string().when("DELIVER_BANK", (deliverType: any, schema: any) => {
            return deliverType && deliverType === YesNo.Yes ? schema.required('此為必填欄位') : schema
          }),
          ADDRESS: Yup.string().when("DELIVER_BANK", (deliverType: any, schema: any) => {
            return deliverType && deliverType === YesNo.Yes ? schema.required('此為必填欄位') : schema
          })
        }));
      }),
      BUILD_ADDRESS_POST_CODE: Yup.string().test('required', '此為必填欄位', function (val) {
        const { path, parent } = this;
        const { CHECKED, DATA_TYPE } = parent;
        let errMsg = '';

        if (CHECKED && DATA_TYPE === EndorseColType.FIRE_ADDRESS && !val) {
          errMsg = '標的物地址-郵遞區號為必填欄位';
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.FIRE_ADDRESS && val && val.length > 6) {
          errMsg = '郵遞區號長度不可超過 6 字元';
        }

        return errMsg ? this.createError({ path, message: errMsg }) : true;
      }),
      BUILD_ADDRESS_CITY_ID: Yup.string().test('required', '標的物地址-縣、市為必填欄位', function (val) {
        const { CHECKED, DATA_TYPE } = this.parent;
        if (CHECKED && DATA_TYPE === EndorseColType.FIRE_ADDRESS) {
          return val;
        }

        return true;
      }),
      BUILD_ADDRESS_AREA_ID: Yup.string().test('required', '標的物地址-區域為必填欄位', function (val) {
        const { CHECKED, DATA_TYPE } = this.parent;
        if (CHECKED && DATA_TYPE === EndorseColType.FIRE_ADDRESS) {
          return val;
        }

        return true;
      }),
      BUILD_ADDRESS_STREET_ID: Yup.string().test('required', '標的物地址-道路為必填欄位', function (val) {
        const { CHECKED, DATA_TYPE } = this.parent;
        if (CHECKED && DATA_TYPE === EndorseColType.FIRE_ADDRESS) {
          return val;
        }

        return true;
      }),
      BUILD_ADDRESS_ROADWAY: Yup.string().test('range 1-9999', '標的物地址-巷，僅接受 1 ~ 9999 之數值', function (val) {
        const { CHECKED, DATA_TYPE } = this.parent;
        if (CHECKED && DATA_TYPE === EndorseColType.FIRE_ADDRESS && val) {
          return /^([1-9]|[1-9][0-9]|[1-9][0-9][0-9]|[1-9][0-9][0-9][0-9])$/.test(val);
        }

        return true;
      }),
      BUILD_ADDRESS_ALLEY: Yup.string().test('range 1-999', '標的物地址-弄，僅接受 1 ~ 999 之數值', function (val) {
        const { CHECKED, DATA_TYPE } = this.parent;
        if (CHECKED && DATA_TYPE === EndorseColType.FIRE_ADDRESS && val) {
          return /^([1-9]|[1-9][0-9]|[1-9][0-9][0-9])$/.test(val);
        }

        return true;
      }),
      BUILD_ADDRESS_NUMBER: Yup.string().test('has error', '檢核錯誤', function (val) {
        const { path, parent } = this;
        const { CHECKED, DATA_TYPE } = parent;
        let errMsg = '';

        if (CHECKED && DATA_TYPE === EndorseColType.FIRE_ADDRESS && !val) {
          errMsg = '標的物地址-門牌號為必填欄位';
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.FIRE_ADDRESS && val && !/^([1-9]|[1-9][0-9]|[1-9][0-9][0-9]|[1-9][0-9][0-9][0-9])$/.test(val)) {
          errMsg = '標的物地址-門牌號，僅接受 1 ~ 9999 之數值';
        }

        return errMsg ? this.createError({ path, message: errMsg }) : true;
      }),
      BUILD_ADDRESS_NUMBER_OF: Yup.string().test('range 1-9999', '標的物地址-門牌號之，僅接受 1 ~ 9999 之數值', function (val) {
        const { CHECKED, DATA_TYPE } = this.parent;
        if (CHECKED && DATA_TYPE === EndorseColType.FIRE_ADDRESS && val) {
          return /^([1-9]|[1-9][0-9]|[1-9][0-9][0-9]|[1-9][0-9][0-9][0-9])$/.test(val);
        }

        return true;
      }),
      BUILD_ADDRESS_FLOOR: Yup.string().test('range 1-101', '標的物地址-樓層，僅接受 1 ~ 101 之數值', function (val) {
        const { CHECKED, DATA_TYPE } = this.parent;
        if (CHECKED && DATA_TYPE === EndorseColType.FIRE_ADDRESS && val) {
          return /^([1-9]|[1-9][0-9]|10[0-1])$/.test(val);
        }

        return true;
      }),
      BUILD_ADDRESS_FLOOR_OF: Yup.string().test('range 1-999', '標的物地址-樓層之，僅接受 1 ~ 999 之數值', function (val) {
        const { CHECKED, DATA_TYPE } = this.parent;
        if (CHECKED && DATA_TYPE === EndorseColType.FIRE_ADDRESS && val) {
          return /^([1-9]|[1-9][0-9]|[1-9][0-9][0-9])$/.test(val);
        }

        return true;
      })
    })
  )
});

const initState: IState = {
  postCodes: new Array(0),
  insCats: new Array(0),
  insType: '',
  applyNo: '',
  policyInfos: new Array(0),
  endorse: {
    OPTION: new Array(0),
    FILES: new Array(0)
  },
  endorseCodes: new Array(0),
  mortgageBanks: new Array(0),
  yesnoSysOpts: new Array(0),
  streetInfos: new Array(0),
  UICodes: new Array(0),
  fireHouseCertificateLinkSysOpts: new Array(0)
}

class CorrectionFire extends React.Component<IProps, IState>{
  private formikRef = React.createRef<FormikProps<IFormValue>>();

  state: IState = initState;

  componentDidMount = async () => {
    const tempVal = storageService.getItem(storageKeys.CorrectionFormVal);
    if (tempVal) {
      const formVal = JSON.parse(tempVal) as IFormValue;

      const fetchPolicyNosreq = {
        INS_TYPE_ID: formVal.INS_TYPE,
        APPLICANT_ID: storageService.getItem(storageKeys.MemberId)
      }

      const initSetting = await service.fetchFireInitSetting(fetchPolicyNosreq);
      if (!initSetting) return;

      const endorse = await service.fetchEndorse(formVal.INS_TYPE, formVal.APPLY_NO);
      if (!endorse) return;

      let streetInfos = new Array(0);
      const fireAddrEndorseOpt = _.find(formVal.ENDORSE_OPTS, (opt: any) => opt.DATA_TYPE === EndorseColType.FIRE_ADDRESS);
      if (fireAddrEndorseOpt) {
        const cityId = fireAddrEndorseOpt.BUILD_ADDRESS_CITY_ID;
        const areaId = fireAddrEndorseOpt.BUILD_ADDRESS_AREA_ID;
        const res = await service.fetchBuildAddr(cityId, areaId);
        if (res) {
          streetInfos = [...res.RD_ST_DATA];
        }
      }

      formInitialValues = {
        ...formInitialValues,
        ...formVal
      };

      this.setState({
        insType: formVal.INS_TYPE,
        applyNo: formVal.APPLY_NO,
        insCats: [...initSetting.inscats],
        postCodes: [...initSetting.postcodes],
        endorseCodes: [...initSetting.endorseCodes],
        mortgageBanks: [...initSetting.mortgageBanks],
        UICodes: [...initSetting.UICodes],
        policyInfos: [...initSetting.policyInfos],
        yesnoSysOpts: [...initSetting.yesnoSysOpts],
        streetInfos: [...streetInfos],
        fireHouseCertificateLinkSysOpts: [...initSetting.fireHouseCertificateLinkSysOpts],
        endorse: endorse
      });

      return;
    }

    let insTypeIdVal: any;
    let policyNoVal: any;
    let applyNoVal: any;
    if (this.props.history.location.state) {
      // 透過路由取得險種類別
      const { insTypeId, policyNo, applyNo }: any = this.props.history.location.state;
      insTypeIdVal = insTypeId;
      policyNoVal = policyNo;
      applyNoVal = applyNo;
    }

    if (!insTypeIdVal || !policyNoVal) {
      this.props.history.replace(RouterSettings.MemberCenter.PolicyInquiry);
      return;
    }

    const fetchPolicyNosreq = {
      INS_TYPE_ID: insTypeIdVal,
      APPLICANT_ID: storageService.getItem(storageKeys.MemberId)
    }

    const initSetting = await service.fetchFireInitSetting(fetchPolicyNosreq);
    if (!initSetting) return;

    const endorse = await service.fetchEndorse(insTypeIdVal, applyNoVal);
    if (!endorse) return;

    this.setState(prevState => {
      return {
        ...prevState,
        postCodes: [...initSetting.postcodes]
      }
    });

    const policyInfo = _.find(initSetting.policyInfos, (policyInfo: ClaimPolicyNoContent) => policyInfo.APPLY_NO === applyNoVal);
    if (policyInfo) {
      formInitialValues = {
        ...formInitialValues,
        FULL_ADDRESS: policyInfo.ADDRESS
      };
    }

    let streetInfos = new Array(0);
    const fireAddrEndorseOpt = _.find(endorse.OPTION, (opt: EndorseOptContent) => opt.DATA_TYPE === EndorseColType.FIRE_ADDRESS);
    if (fireAddrEndorseOpt) {
      const cityId = this.handleGetOriginalVal(fireAddrEndorseOpt.TARGET, EndorseTarget.CITY_ID);
      const areaId = this.handleGetOriginalVal(fireAddrEndorseOpt.TARGET, EndorseTarget.AREA_ID);
      const res = await service.fetchBuildAddr(cityId, areaId);
      if (res) {
        streetInfos = [...res.RD_ST_DATA];
      }
    }

    formInitialValues = {
      ...formInitialValues,
      INS_TYPE: insTypeIdVal,
      POLICY_NUMBER: policyInfo ? policyInfo.POLICY_NO : policyNoVal,
      APPLY_NO: policyInfo ? policyInfo.APPLY_NO : applyNoVal,
      ENDORSE_OPTS: this.handleGetEndorseOpts(endorse.OPTION),
      ORIGINAL_ENDORSE: endorse
    };

    this.setState(prevState => {
      return {
        ...prevState,
        insType: insTypeIdVal,
        applyNo: applyNoVal,
        insCats: [...initSetting.inscats],
        endorseCodes: [...initSetting.endorseCodes],
        mortgageBanks: [...initSetting.mortgageBanks],
        UICodes: [...initSetting.UICodes],
        policyInfos: [...initSetting.policyInfos],
        yesnoSysOpts: [...initSetting.yesnoSysOpts],
        streetInfos: [...streetInfos],
        fireHouseCertificateLinkSysOpts: [...initSetting.fireHouseCertificateLinkSysOpts],
        endorse: endorse
      }
    });
  }

  componentWillUnmount() {
    if (this.props.history.location &&
      this.props.history.location.pathname &&
      this.props.history.location.pathname !== RouterSettings.MemberCenter.CorrectionPreview) {
      storageService.removeItem(storageKeys.CorrectionFormVal);
    }
  }

  /**
  * @description 取得項目原始資料值
  */
  handleGetOriginalVal = (targets: EndorseOptContentTarget[], targetType: string) => {
    return _.find(targets,
      (target: EndorseOptContentTarget) => target.ENDORSE_TARGET === targetType)?.ORG_VALUE || '';
  };

  handleGetEndorseOpts = (opts: EndorseOptContent[]) => {
    return _.map(opts, (opt: EndorseOptContent) => {
      return {
        CHECKED: false,
        ENDORSE_TYPE: opt.ENDORSE_TYPE,
        DATA_TYPE: opt.DATA_TYPE,
        ORDER_NO: opt.ORDER_NO,
        TYPE_NAME: opt.TYPE_NAME,
        ATTACHMENT: opt.ATTACHMENT,
        ATTACHMENT_NOTE: opt.ATTACHMENT_NOTE,
        ATTACHMENT_NAME: opt.ATTACHMENT_NAME,
        FILE_FIELD_NAME: '',
        FILE_BASE64: '',
        ...this.handleGetOriginalInfo(opt)
      };
    });
  };

  /**
   * @description 取得可變更項目舊資料設定
  */
  handleGetOriginalInfo = (opt: EndorseOptContent) => {
    switch (opt.ENDORSE_TYPE) {
      case EndorseType.CONTACT_ADDRESS:
        return {
          POST_CODE: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.POSTCODE),
          CITY_ID: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.CITY_ID),
          AREA_ID: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.AREA_ID),
          ADDRESS: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.ADDRESS),
          CITY_ID_NAME: commonService.getCityName(this.state.postCodes, this.handleGetOriginalVal(opt.TARGET, EndorseTarget.CITY_ID)),
          AREA_ID_NAME: commonService.getAreaName(this.state.postCodes, this.handleGetOriginalVal(opt.TARGET, EndorseTarget.CITY_ID), this.handleGetOriginalVal(opt.TARGET, EndorseTarget.AREA_ID))
        };
      case EndorseType.CONTACT_MOBILE:
        return {
          MOBILE: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.MOBILE)
        };
      case EndorseType.ADD_MORTGAGE:
        return {
          MORGAGE_INFOS: this.handleGetMortgageInfos(opt.DATA)
        };
      case EndorseType.HOUSE_NUMBER:
        return {
          BUILD_ADDRESS_POST_CODE: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.POSTCODE),
          BUILD_ADDRESS_CITY_ID: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.CITY_ID),
          BUILD_ADDRESS_AREA_ID: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.AREA_ID),
          BUILD_ADDRESS_CITY_NAME: commonService.getCityName(this.state.postCodes, this.handleGetOriginalVal(opt.TARGET, EndorseTarget.CITY_ID)),
          BUILD_ADDRESS_AREA_NAME: commonService.getAreaName(this.state.postCodes, this.handleGetOriginalVal(opt.TARGET, EndorseTarget.CITY_ID), this.handleGetOriginalVal(opt.TARGET, EndorseTarget.AREA_ID)),
          BUILD_ADDRESS_STREET_ID: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.ADDRESS_RD_ST_CODE),
          BUILD_ADDRESS_STREET_ID_NAME: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.ADDRESS_RD_ST),
          BUILD_ADDRESS_ROADWAY: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.ADDRESS_LN),
          BUILD_ADDRESS_ALLEY: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.ADDRESS_ALY),
          BUILD_ADDRESS_NUMBER: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.ADDRESS_NO_1),
          BUILD_ADDRESS_NUMBER_OF: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.ADDRESS_NO_2),
          BUILD_ADDRESS_FLOOR: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.ADDRESS_F_1),
          BUILD_ADDRESS_FLOOR_OF: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.ADDRESS_F_2),
          BUILD_ADDRESS_REMARK: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.ADDRESS_NOTE)
        };
      default:
        return {};
    }
  }

  /**
 * @description 取得抵押權人資訊
 */
  handleGetMortgageInfos = (datas: EndorseOptContentData[]) => {
    if (!datas || datas.length === 0) return new Array(0);

    return _.map(datas, (data: EndorseOptContentData) => {
      let hdrIdName = '';
      let hbrchIdName = '';
      const bankInfo = _.find(this.state.mortgageBanks, (mortgageBank: MortgageBankContent) => mortgageBank.HDR_ID === data.HDR_ID);
      if (bankInfo) {
        hdrIdName = bankInfo.HDR_NAME;
        const branchInof = _.find(bankInfo.BANK_CATEGORY, (category: MortgageBankCategoryContent) => category.HBRCH_ID === data.HBRCH_ID);
        if (branchInof) {
          hbrchIdName = branchInof.HBRCH_NAME;
        }
      }
      return {
        HDR_ID_CODE: data.HDR_ID,
        HDR_ID: data.HDR_ID,
        HDR_ID_NAME: hdrIdName,
        HBRCH_ID: data.HBRCH_ID,
        HBRCH_ID_NAME: hbrchIdName,
        DELIVER_BANK: data.DELIVER_BANK && data.DELIVER_BANK === YesNoType.Yes
          ? YesNo.Yes
          : data.DELIVER_BANK && data.DELIVER_BANK === YesNoType.No
            ? YesNo.No
            : '',
        OPERATOR: data.OPERATOR,
        POSTCODE: data.POSTCODE,
        CITY_ID: data.CITY_ID,
        CITY_ID_NAME: commonService.getCityName(this.state.postCodes, data.CITY_ID),
        AREA_ID_NAME: commonService.getAreaName(this.state.postCodes, data.CITY_ID, data.AREA_ID),
        AREA_ID: data.AREA_ID,
        ADDRESS: data.ADDRESS,
      }
    })
  };

  render() {
    /**  
     * @description 表單 Submit 事件
    */
    const handleSubmit = (val: IFormValue) => {
      // console.log('val', val)
      storageService.setItem(storageKeys.CorrectionFormVal, JSON.stringify(val));

      const path = {
        pathname: RouterSettings.MemberCenter.CorrectionPreview,
        state: { correctionFormVal: val }
      }

      this.props.history.push(path);
    }

    /** 
    * @description 取得保單號碼選單
    */
    const handleGetPolicyNoOpts = () => {
      if (!this.state.policyInfos || this.state.policyInfos.length === 0) return;

      return _.map(this.state.policyInfos, (policyInfo: ClaimPolicyNoContent, index: number) => {
        if (!policyInfo.POLICY_NO) return;

        return <option value={policyInfo.POLICY_NO} key={index}>{policyInfo.POLICY_NO}</option>
      })
    }

    /**
     * @description 保單號碼變更事件
     */
    const handlePolicyNoChange = async (val: string, setFieldValue: any) => {
      if (!val) return;

      // 取得保單資訊
      const policyInfo = _.find(this.state.policyInfos, (policyInfo: ClaimPolicyNoContent) => policyInfo.POLICY_NO === val);
      if (!policyInfo) return;

      // 取得可變更項目
      const endorse = await service.fetchEndorse(this.state.insType, policyInfo.APPLY_NO);
      if (!endorse) return;

      const endorseOpts = this.handleGetEndorseOpts(endorse.OPTION);

      setFieldValue("APPLY_NO", policyInfo.APPLY_NO);
      setFieldValue("FULL_ADDRESS", policyInfo.ADDRESS);
      setFieldValue("ORIGINAL_ENDORSE", endorse);
      setFieldValue("ENDORSE_OPTS", endorseOpts);
    }

    return (
      <div className="correction">
        <div className="template-container">
          <div className="template-content">
            <div className="correction__title">批改申請</div>
            <div className="correction__content">
              <Formik
                innerRef={this.formikRef as any}
                initialValues={formInitialValues}
                validationSchema={formValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ values, errors, touched, handleBlur, handleChange, setFieldValue, setFieldTouched, setFieldError }) => (
                  <Form>
                    <div className="correction__form">
                      <div className="element__form-table">
                        <div className="element__form-table-tr">
                          <div className="element__form-table-td">
                            {/* 險種 */}
                            <div className="element__form-group">
                              <div className="element__form-group-title" data-fieldname="INS_TYPE">險種</div>
                              <div className="element__form-group-content">
                                {
                                  this.state.insCats && this.state.insCats.length > 0 && (
                                    _.map(this.state.insCats, (inscat: Inscat, index: number) => {
                                      if (inscat.INS_TYPE_ID === InsType.MobileDevice) return;
                                      return (
                                        <div className="element__radio-group" key={index}>
                                          <label>
                                            <div className="element__radio">
                                              <Field
                                                type="radio"
                                                name="INS_TYPE"
                                                disabled
                                                value={inscat.INS_TYPE_ID}
                                              />
                                              <div className="element__radio-btn" />
                                            </div>
                                            <span>{inscat.INS_TYPE_NAME}</span>
                                          </label>
                                        </div>
                                      );
                                    })
                                  )
                                }
                              </div>
                              <div className="element__error-message">
                                <ErrorMessage name="INS_TYPE" />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="element__form-table-tr">
                          <div className="element__form-table-td">
                            <div className="element__form-group correction__form__info-container">
                              <div className="element__form-group-content correction__form__info-content">
                                <div className="correction__form__info-content-group">
                                  <p>險種</p>
                                  <p>{InsTypeDefines[this.state.insType as InsType]}</p>
                                </div>
                                <div className="correction__form__info-content-group">
                                  <p>保單號碼*</p>
                                  <Field
                                    as="select"
                                    name="POLICY_NUMBER"
                                    id="policy-number-input"
                                    className="element__select"
                                    data-fieldname="POLICY_NUMBER"
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                      handlePolicyNoChange(e.currentTarget.value, setFieldValue);
                                      handleChange(e);
                                    }}
                                  >
                                    <option value="">請選擇</option>
                                    {
                                      handleGetPolicyNoOpts()
                                    }
                                  </Field>
                                  <div className="element__error-message">
                                    <ErrorMessage name="POLICY_NUMBER" />
                                  </div>
                                </div>
                                <div className="correction__form__info-content-group">
                                  <p>標的物地址</p>
                                  <p>{values.FULL_ADDRESS}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="element__form-table-tr">
                          <div className="element__form-table-td">
                            {/* 變更項目 */}
                            <div className="element__form-group">
                              <div className="element__form-group-title" data-fieldname="ENDORSE_OPTS" >變更項目</div>
                              <div className="element__form-group-content">
                                <div className="correction__list">
                                  <FieldArray name="ENDORSE_OPTS">
                                    {
                                      () => (
                                        <>
                                          {
                                            values.ENDORSE_OPTS && values.ENDORSE_OPTS.length > 0 && (
                                              _.map(values.ENDORSE_OPTS, (opt: EndorseOption, index: number) => {
                                                // console.log('opt', opt)
                                                return (
                                                  <CorrectionEndorseItem
                                                    insTypeId={this.state.insType}
                                                    endorseOpt={opt}
                                                    endorseOptIndex={index}
                                                    key={index}
                                                    values={values}
                                                    errors={errors}
                                                    touched={touched}
                                                    setFieldValue={setFieldValue}
                                                    setFieldTouched={setFieldTouched}
                                                    setFieldError={setFieldError}
                                                    handleChange={handleChange}
                                                    handleBlur={handleBlur}
                                                    postCodes={this.state.postCodes}
                                                    emailDomains={[]}
                                                    yesnoSysOpts={this.state.yesnoSysOpts}
                                                    rdStDataContent={this.state.streetInfos}
                                                    mortgageBanks={this.state.mortgageBanks}
                                                    marriageSysOpts={[]}
                                                    UICodes={this.state.UICodes}
                                                    fireHouseCertificateLinkSysOpts={this.state.fireHouseCertificateLinkSysOpts}
                                                  />
                                                );
                                              })
                                            )
                                          }
                                        </>
                                      )
                                    }
                                  </FieldArray>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="correction__remark">
                      <div className="correction__remark-title">備註</div>
                      <ol>
                        {
                          this.state.endorseCodes && this.state.endorseCodes.length > 0 && (
                            _.map(this.state.endorseCodes, (code: Code, index: number) => <li key={index}>{code.CODE_VALUE}</li>)
                          )
                        }
                      </ol>
                    </div>
                    <div className="correction__controls">
                      <button type="submit" className="element__button element__button--red">預覽畫面</button>
                      <button
                        type="button"
                        className="element__button element__button--red-border"
                        onClick={() => {
                          this.props.history.replace(RouterSettings.MemberCenter.PolicyInquiry);
                        }}
                      >
                        取消
                    </button>
                    </div>
                    <FormikFocusError />
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default CorrectionFire;
